import { Trash } from "@phosphor-icons/react";

import { SplitButton } from "common/components/SplitButton";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import type { RentedSubscription } from "common/types";

import { formatBillingDate } from "../../utils";
import NonRenewingTag from "../NonRenewingTag";
import WorkspaceSubRenewalPrice from "../OutreachPlan/WorkspaceSubRenewalPrice";
import RemoveChangeButton from "../RemoveChangeButton";
import SubscriptionInfo from "../SubscriptionInfo";
import Accounts from "./Accounts";
import ScheduledChangeTag from "./ScheduledChangeTag";

interface AvatarOrderProps {
  orderNumber: number;
  subscription: RentedSubscription;
  onManageClick: () => void;
  onCancelSubscriptionClick: () => void;
}

export default function AvatarOrder({
  orderNumber,
  subscription,
  onManageClick,
  onCancelSubscriptionClick,
}: AvatarOrderProps) {
  const { status, item_price: itemPrice, has_changes, id } = subscription;
  const { permissions } = useSelectedWorkspaceContext();

  const isScheduledCancellation = status === "non_renewing";
  const removeChangeType = isScheduledCancellation ? "cancellation" : "change";
  const showEndState = isScheduledCancellation || has_changes;
  const cardLabel = showEndState ? "No further charges" : "Upcoming invoice";
  const dateLabel = showEndState ? "Ends on" : "Renews on";

  const {
    cancelled_at: cancelledAt,
    next_billing: nextBilling,
    rented_count: rentedCount,
  } = subscription;

  // Get the billing date for the subscription
  const billingDate = formatBillingDate(
    (isScheduledCancellation ? cancelledAt : nextBilling) * 1000,
  );

  return (
    <section className="w-full grid lg:grid-cols-2 gap-x-2 p-1.5 bg-whiteGray rounded-5xl border border-black-200">
      <div className="flex flex-col gap-6 p-5 justify-between">
        <div className="flex flex-row items-center justify-between gap-3">
          <span className="text-lg-bold">Avatar Order #{orderNumber}</span>

          <div className="flex flex-row items-center gap-2">
            {itemPrice.period === 6 && (
              <Tag variant="secondary">6 months deal</Tag>
            )}

            {status === "non_renewing" && <NonRenewingTag type="avatar" />}

            {has_changes && status !== "non_renewing" && (
              <ScheduledChangeTag
                subscriptionId={id}
                rentedCount={rentedCount}
                type="avatar"
              />
            )}
          </div>
        </div>

        <Accounts subscriptionId={id} rentedCount={rentedCount} />

        <div className="ml-auto">
          {showEndState ? (
            <RemoveChangeButton
              subscription={subscription}
              type={removeChangeType}
              isInitiallyDisabled={false}
            />
          ) : (
            <SplitButton
              size="sm"
              onClick={onManageClick}
              dropdownMenuItems={[
                {
                  label: "Cancel order",
                  icon: Trash,
                  onSelect: onCancelSubscriptionClick,
                  variant: "danger",
                },
              ]}
              disabled={!permissions.includes("billing_full")}
            >
              Manage
            </SplitButton>
          )}
        </div>
      </div>

      {/* TODO: Replace with SubscriptionBillingPanel (?) */}
      <div className="p-1 bg-white rounded-4xl border border-black-200">
        <div className="h-full border border-black-100 rounded-3xl flex flex-col p-5 gap-3">
          <div className="flex flex-row justify-between gap-4 items-start relative">
            <div className="flex flex flex-col gap-4">
              <span className="text-xs-medium text-black-500">{cardLabel}</span>

              <div className="flex flex-col gap-1">
                <span className="text-xxs text-black-500">{dateLabel}</span>
                <span className="text-xs-bold">{billingDate}</span>
              </div>
            </div>

            <WorkspaceSubRenewalPrice
              subscriptionId={id}
              pricingPeriodInfo={itemPrice}
              isScheduledCancellation={isScheduledCancellation}
            />
          </div>

          <div className="border-t-2 border-dotted border-black-200" />

          <div className="flex flex-col gap-4">
            <span className="text-xs-medium text-black-500">Payment</span>

            <SubscriptionInfo isLoading={false} subscription={subscription} />
          </div>
        </div>
      </div>
    </section>
  );
}
